import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  useMediaQuery,
  Stack,
  Chip,
  Fade,
  Skeleton,
  TablePagination,
} from '@mui/material';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useNavigate } from 'react-router-dom';
import config from '../../Config';

export default function UserLeads() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchLeads = async (pageNumber = 1, limit = rowsPerPage) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${config.apiBaseUrl}/admin/get-leads?page=${pageNumber}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok && data.success && Array.isArray(data.data)) {
        setLeads(data.data);
        setTotalCount(data.total || data.data.length);
      } else {
        setLeads([]);
        setTotalCount(0);
      }
    } catch (error) {
      setLeads([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads(page + 1, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const renderLoadingSkeleton = () => (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
      <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
      <Stack spacing={2}>
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
        ))}
      </Stack>
    </Paper>
  );

  const renderTable = () => (
    <Fade in={!loading}>
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }}
      >
        <Box sx={{ p: 3, background: 'white' }}>
          <Stack direction="row" alignItems="center" spacing={2} mb={3}>
            <ContactMailIcon color="primary" />
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              Leads Management
            </Typography>
            <Chip label={`${totalCount} Total`} color="primary" variant="outlined" size="small" />
          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Organisation
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Message</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Created At
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {leads.map((lead) => (
                  <TableRow
                    key={lead._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f1f5f9',
                        transition: 'background-color 0.2s ease-in-out',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary"
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => navigate(`/lead-detail/${lead._id}`)}
                      >
                        {lead.name || '-'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{lead.email || '-'}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{lead.number || '-'}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{lead.organisation || '-'}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ maxWidth: 250, wordBreak: 'break-word' }}
                      >
                        {lead.message || '-'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontSize="12px">
                        <CalendarTodayIcon
                          fontSize="inherit"
                          sx={{ mr: 0.5, verticalAlign: 'middle' }}
                        />
                        {formatDate(lead.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {leads.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Stack alignItems="center" spacing={2}>
                        <ContactMailIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                        <Typography variant="h6" color="text.secondary">
                          No leads found
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          Leads submitted from the website will appear here.
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
          />
        </Box>
      </Paper>
    </Fade>
  );

  return (
    <Box sx={{ px: 3, py: 2, minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Typography
        variant={isSmDown ? 'h5' : 'h4'}
        fontWeight="bold"
        mb={4}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        User Leads
      </Typography>

      {loading ? renderLoadingSkeleton() : renderTable()}
    </Box>
  );
}
